import { useContext, useEffect, useMemo, useState } from "react";
import { useQuery }                                   from "react-query";

import moment from 'moment';

import config              from "../../../common/Config";
import useInternalFetch    from "../../../api/apiInternal";
import useFetch            from "../../../api/api";
import PortfolioContext    from "../context";

const assetTypeOrder = ['ACAO', 'STOCK', 'FII', 'TESOURODIRETO', 'BOND', 'CASH'];

const labelMap = {
	ACAO			: 'Ações',
	STOCK			: 'Stocks',
	FII				: 'FIIs',
	TESOURODIRETO	: 'Tesouro Direto',
	BOND			: 'Bonds',
	CASH			: 'Reserva'
};

const defaultAssetClasses = Object.entries(labelMap).map(([assetType, label]) => ({
	assetType,
	label,
	planned	: 0,
	current	: 0,
	diff	: 0
}));

const sortByAssetTypeOrder = (list) => {
	const orderMap = assetTypeOrder.reduce((acc, type, idx) => {
		acc[type] = idx;
		return acc;
	}, {});
	return [...list].sort((a, b) => {
		const aPos = orderMap[a.assetType] ?? assetTypeOrder.length;
		const bPos = orderMap[b.assetType] ?? assetTypeOrder.length;
		return aPos - bPos;
	});
};

const usePortfolioAllocations = () => {
	const internalFetch = useInternalFetch()
	const privateFetch  = useFetch()

	const { allowFailedRiskGrowth, investmentIntervalDays, investByDate } = useContext(PortfolioContext)

	const [selectedClass, setSelectedClass] 		= useState(defaultAssetClasses[0].assetType)
	const [targetAllocations, setTargetAllocations]	= useState(defaultAssetClasses)
	const [portfolio, setPortfolio] 				= useState([])
	const [selectedPortfolio, setSelectedPortfolio]	= useState([])

	useEffect(() => {
		const loadData = async () => {
			// Busca targets por classe
			const targetsResp = await internalFetch(config.apiUrl + '/api/portfolio/assetTypes');
			let targets = [];
			if (targetsResp?.status === 200 && Array.isArray(targetsResp.data)) {
				targets = targetsResp.data;
			}

			const sortedTargets = sortByAssetTypeOrder(targets.length ? targets : defaultAssetClasses);

			const baseAllocations = sortedTargets.map(item => ({
				assetType	: item.assetType,
				label		: labelMap[item.assetType] || item.label || item.assetType,
				planned		: Number(item.target ?? item.planned ?? 0),
				current		: 0,
				diff		: 0
			}));

			// Busca resumo com valores atuais
			const summaryResp = await internalFetch(config.apiUrl + '/api/portfolio/summary/withRates');
			if (!summaryResp || summaryResp.status !== 200 || !Array.isArray(summaryResp.data)) {
				setTargetAllocations(baseAllocations);
				setPortfolio([]);
				setSelectedPortfolio([]);
				return;
			}

			const data = summaryResp.data;

			const totalPerAssetType = data.reduce((acc, r) => {
				const key = r.assetType;
				const val = Number(r.value ?? 0);
				acc[key] = (acc[key] || 0) + val;
				return acc;
			}, {});

			const portfolioWithPercent = data.map(r => {
				const totalAsset = totalPerAssetType[r.assetType] || 0;
				const percent = totalAsset > 0 ? (Number(r.value ?? 0) / totalAsset) * 100 : 0;
				return {
					...r,
					percentage	: percent,
					diff		: r.target ? (Number(r.target) - percent) : 0
				};
			});

			setPortfolio(portfolioWithPercent);

			const total = data.reduce((acc, r) => acc + Number(r.value ?? 0), 0);

			const updatedAllocations = baseAllocations.map(c => {
				const currentAsset   = data.filter(r => r.assetType === c.assetType);
				const totalAsset     = currentAsset.reduce((acc, r) => acc + Number(r.value ?? 0), 0);
				const currentPercent = currentAsset && total > 0 ? ((totalAsset / total) * 100).toFixed(2) : 0;
				return {
					...c,
					current	: Number(currentPercent),
					diff	: Number(currentPercent) - c.planned
				};
			});

			setTargetAllocations(updatedAllocations);
			const initialClass = updatedAllocations[0]?.assetType || defaultAssetClasses[0].assetType;
			setSelectedClass(initialClass);
			setSelectedPortfolio(portfolioWithPercent.filter(d => d.assetType === initialClass));
		};

		loadData();
	}, []);

	useEffect(() => {
		setSelectedPortfolio(portfolio.filter(d => d.assetType === selectedClass))
	}, [selectedClass, portfolio])

	// ── Dados de guidance (action + lastPurchaseDate) ─────────────────────────
	// Usa as mesmas query keys do TabList — compartilha cache automaticamente

	const fetchGuidance = (assetType) => () =>
		privateFetch(config.apiUrl + '/api/portfolio/guidance/' + assetType)
			.then(r => r.json())
			.then(r => Array.isArray(r?.data) ? r.data : [])

	const { data: guidanceAcao  = [] } = useQuery({
		queryKey		: ['portfolioGuidance_ACAO'],
		queryFn			: fetchGuidance('ACAO'),
		refetchInterval	: false,
	})

	const { data: guidanceStock = [] } = useQuery({
		queryKey		: ['portfolioGuidance_STOCK'],
		queryFn			: fetchGuidance('STOCK'),
		refetchInterval	: false,
	})

	const guidanceMap = useMemo(() => {
		const map = {}
		;[...guidanceAcao, ...guidanceStock].forEach(r => { map[r.ticker] = r })
		return map
	}, [guidanceAcao, guidanceStock])

	// ── Dados de growthRisk (cache do TabList; sem fetch adicional) ────────────
	// enabled: false → não faz request, mas re-renderiza quando TabList atualiza o cache

	const { data: assetsAcao  = [] } = useQuery({
		queryKey	: ['portfolio_ACAO'],
		queryFn		: () => Promise.resolve([]),
		enabled		: false,
	})
	const { data: assetsStock = [] } = useQuery({
		queryKey	: ['portfolio_STOCK'],
		queryFn		: () => Promise.resolve([]),
		enabled		: false,
	})

	const assetsMap = useMemo(() => {
		const map = {}
		;[...assetsAcao, ...assetsStock].forEach(r => { map[r.ticker] = r })
		return map
	}, [assetsAcao, assetsStock])

	// ── Portfolio filtrável para sugestão de aporte ───────────────────────────

	const fullPortfolio = useMemo(() => {
		return portfolio.map(p => {
			const ticker   = p.identificator
			const guidance = guidanceMap[ticker]

			if (guidance !== undefined && guidance.action !== 'comprar') {
				return { ...p, investable: false, excludedReason: `Ativo não está marcado para compra (Ação: ${guidance.action})` }
			}

			if (!allowFailedRiskGrowth) {
				const asset = assetsMap[ticker]
				if (asset !== undefined && asset.growthRisk === 'Reprovado') {
					return { ...p, investable: false, excludedReason: 'Risco x Cresc.: Reprovado' }
				}
			}

			if (investmentIntervalDays > 0 && guidance?.lastPurchaseDate) {
				const daysSinceLast = moment().diff(moment(guidance.lastPurchaseDate), 'days')
				if (daysSinceLast < investmentIntervalDays) {
					return { ...p, investable: false, excludedReason: `Último aporte: ${daysSinceLast}d atrás (mín. ${investmentIntervalDays}d)` }
				}
			}

			return { ...p, investable: true, excludedReason: null }
		})
	}, [portfolio, guidanceMap, assetsMap, allowFailedRiskGrowth, investmentIntervalDays])

	const investablePortfolio = useMemo(() => {
		return fullPortfolio.filter(p => p.investable)
	}, [fullPortfolio])

	// ── Alocações ─────────────────────────────────────────────────────────────

	const totalTarget    = targetAllocations.reduce((acc, item) => acc + item.planned, 0)
	const remainingTarget = 100 - totalTarget

	const saveAssetAllocations = () => {
		const payload = targetAllocations.map(item => ({
			assetType	: item.assetType,
			label		: item.label,
			value		: item.planned
		}))

		const options = {
			method	: 'POST',
			headers	: { 'content-type': 'application/json; charset=utf-8' },
			body	: JSON.stringify(payload)
		}

		return internalFetch(config.apiUrl + '/api/portfolio/assetTypes', options)
	}

	return {
		selectedClass,
		setSelectedClass,
		targetAllocations,
		setTargetAllocations,
		portfolio,
		fullPortfolio,
		investablePortfolio,
		selectedPortfolio,
		guidanceMap,
		investByDate,
		remainingTarget,
		saveAssetAllocations
	}
}

export default usePortfolioAllocations
